import { createContext, ReactNode, useCallback, useContext, useEffect, useState, useRef } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { EnvVars } from 'env';

type ReCAPTCHAProps = {
  recaptcha: string;
  handleVerifyRecaptcha: (token: string) => void;
  refreshRecaptcha: string | number | boolean;
  setRefreshRecaptcha: (state: string | number | boolean) => void;
  isRecaptchaReady: boolean;
  clearRecaptcha: () => void;
};

type ReCAPTCHAProviderProps = {
  children: ReactNode;
};

const ReCAPTCHAContext = createContext<ReCAPTCHAProps>({} as ReCAPTCHAProps);

export function ReCAPTCHAProvider({ children }: ReCAPTCHAProviderProps) {
  const { RECAPTCHA_SITE_KEY } = EnvVars;
  const [recaptcha, setRecaptcha] = useState('');
  const [refresh, setRefresh] = useState<string | number | boolean>(Date.now());
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleVerifyRecaptcha = useCallback((token: string) => {
    if (token && token.length > 0) {
      setRecaptcha(token);
      setIsRecaptchaReady(true);
    } else {
      console.error('Token do reCAPTCHA inválido:', token);
      setIsRecaptchaReady(false);
    }
  }, []);

  const setRefreshRecaptcha = useCallback((state: string | number | boolean) => {
    setRefresh(state);
  }, []);

  const clearRecaptcha = useCallback(() => {
    setRecaptcha('');
    setIsRecaptchaReady(false);
  }, []);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setRefreshRecaptcha(Date.now());
      setRecaptcha('');
      setIsRecaptchaReady(false);
    }, 90000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [setRefreshRecaptcha]);

  if (!RECAPTCHA_SITE_KEY) {
    console.error('RECAPTCHA_SITE_KEY não está definida!');
    return <>{children}</>;
  }

  return (
    <ReCAPTCHAContext.Provider
      value={{
        handleVerifyRecaptcha,
        recaptcha,
        refreshRecaptcha: refresh,
        setRefreshRecaptcha,
        isRecaptchaReady,
        clearRecaptcha,
      }}
    >
      <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
        {children}
      </GoogleReCaptchaProvider>
    </ReCAPTCHAContext.Provider>
  );
}

export function useReCAPTCHA(): ReCAPTCHAProps {
  const context = useContext(ReCAPTCHAContext);

  if (!context) {
    throw new Error('useReCAPTCHA must be used within an ReCAPTCHAProvider');
  }

  return context;
}
